<template>
  <div
    :class="[
      'q-text-input',
      `q-text-input--${size}`,
      `q-text-input--${variant}`,
      {
        'q-text-input--error': error,
        'q-text-input--disabled': disabled,
        'q-text-input--readonly': readonly,
        'q-text-input--focused': focused,
        'q-text-input--hover': hover,
        'q-text-input--full-width': fullWidth,
        'q-text-input--with-copy': type === 'copy',
      },
    ]"
  >
    <div class="q-text-input__wrapper">
      <input
        ref="inputRef"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :type="inputType"
        :inputmode="numInputMode"
        :min="min"
        :max="max"
        :maxlength="maxlength"
        class="q-text-input__input"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      />
      <v-icon
        v-if="type === 'copy'"
        name="copy-simple"
        :width="24"
        :height="24"
        stroke="#9b9b9b"
        class="q-text-input__copy-icon"
        pointer
        @click="handleCopyClick"
      />
    </div>
    <div
      v-if="error"
      class="q-text-input__error"
    >
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import i18n from '@/i18n';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'm',
    validator: (value) => ['l', 'm', 's'].includes(value),
  },
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'tertiary'].includes(value),
  },
  type: {
    type: String,
    default: 'text',
    validator: (value) => ['text', 'password', 'email', 'number', 'copy'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  hover: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  min: {
    type: [Number, String],
    default: undefined,
  },
  max: {
    type: [Number, String],
    default: undefined,
  },
  precision: {
    type: Number,
    default: undefined,
  },
  maxlength: {
    type: Number,
    default: undefined,
  },
  thousandsSeparator: {
    type: String,
    default: ' ',
  },
  decimalSeparator: {
    type: String,
    default: '.',
  },
  integerOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'copy']);

const focused = ref(false);
const inputRef = ref(null);
const internalValue = ref(props.modelValue ?? '');

watch(
  () => props.modelValue,
  (val) => {
    internalValue.value = String(val ?? '');
  }
);

// Текущая локаль из i18n (реактивно)
const currentLocale = computed(() => i18n.global.locale);

// Получение локалезависимых разделителей через Intl.NumberFormat
const getSeparators = (locale) => {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(1000.1);
    const group = parts.find((p) => p.type === 'group')?.value ?? ' ';
    const decimal = parts.find((p) => p.type === 'decimal')?.value ?? '.';
    return { group, decimal };
  } catch (e) {
    return { group: ' ', decimal: '.' };
  }
};
const localeSeparators = computed(() => getSeparators(currentLocale.value));

// Helper to escape regex special chars
const reEscape = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Utilities to format and strip thousand separators using i18n locale
const stripThousands = (value) => {
  const sep = localeSeparators.value.group;
  if (!sep) return String(value ?? '');
  const re = new RegExp(reEscape(sep), 'g');
  return String(value ?? '').replace(re, '');
};
const formatThousands = (value) => {
  const { group: sep, decimal: dec } = localeSeparators.value;
  const normalized = stripThousands(value).replace(new RegExp(reEscape(dec), 'g'), '.');
  const str = String(normalized ?? '');
  if (!str) return '';
  const isNegative = str.startsWith('-');
  const raw = isNegative ? str.slice(1) : str;
  const [intPart, decPart = ''] = raw.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const formatted = props.integerOnly
    ? formattedInt
    : decPart
      ? `${formattedInt}${dec}${decPart}`
      : formattedInt;
  return isNegative ? `-${formatted}` : formatted;
};

// Computed input type: toggle text/number for formatting on blur
const inputType = computed(() => {
  if (props.type === 'copy') return 'text';
  if (props.type === 'number') {
    // Keep as text to allow formatted value on blur
    return 'text';
  }
  return props.type;
});

const numInputMode = computed(() => {
  if (props.type === 'number') {
    return props.integerOnly ? 'numeric' : 'decimal';
  }
  return undefined;
});

// Display value: formatted on blur for numbers, raw otherwise
const displayValue = computed(() => {
  if (props.type === 'number' && !focused.value) {
    void localeSeparators.value.group;
    return formatThousands(internalValue.value);
  }
  return internalValue.value;
});

// Helper: sanitize during input — lenient, preserves partial edits
const sanitizeNumberInput = (val) => {
  const { group: sep, decimal: dec } = localeSeparators.value;
  let v = String(val ?? '');
  // Remove thousands separators
  if (sep) v = v.replace(new RegExp(reEscape(sep), 'g'), '');
  // Convert locale decimal separator to '.' for internal handling
  if (dec && dec !== '.') v = v.replace(new RegExp(reEscape(dec), 'g'), '.');
  // Strip unwanted chars, keep digits, '.', and '-'
  v = v.replace(/[^\d.-]/g, '');
  // keep only single leading minus
  v = v.replace(/(?!^)-/g, '');
  // single decimal point
  v = v.replace(/\.(?=.*\.)/g, '');

  // enforce integer-only or precision=0 as integer during input
  if (props.integerOnly || props.precision === 0) {
    v = v.split('.')[0];
    return v;
  }

  // limit decimal length but do not pad/truncate the integer
  if (props.precision !== undefined && props.precision !== null) {
    const [intPart, decPart = ''] = v.split('.');
    v = intPart + (decPart ? '.' + decPart.slice(0, props.precision) : (v.includes('.') ? '.' : ''));
  }
  return v;
};

// Helper: finalize on blur — strict clamp/precision formatting
const finalizeNumber = (val) => {
  const { group: sep, decimal: dec } = localeSeparators.value;
  let v = String(val ?? '');
  // Remove thousands separators
  if (sep) v = v.replace(new RegExp(reEscape(sep), 'g'), '');
  // Convert locale decimal separator to '.' for internal handling
  if (dec && dec !== '.') v = v.replace(new RegExp(reEscape(dec), 'g'), '.');
  // Strip unwanted chars, keep digits, '.', and '-'
  v = v.replace(/[^\d.-]/g, '');
  // keep only single leading minus
  v = v.replace(/(?!^)-/g, '');
  // single decimal point
  v = v.replace(/\.(?=.*\.)/g, '');

  // empty or just '-' -> return empty
  if (v === '' || v === '-') return '';

  let num = Number(v);
  if (Number.isNaN(num)) return '';

  // clamp to min/max
  const hasMin = props.min !== undefined && props.min !== null && props.min !== '';
  const hasMax = props.max !== undefined && props.max !== null && props.max !== '';
  if (hasMin) num = Math.max(num, Number(props.min));
  if (hasMax) num = Math.min(num, Number(props.max));

  // finalize precision
  if (props.integerOnly || props.precision === 0) {
    return String(Math.trunc(num));
  }
  if (props.precision !== undefined && props.precision !== null) {
    return Number.isFinite(num) ? Number(num).toFixed(props.precision) : '';
  }
  return String(num);
};

const handleInput = (event) => {
  if (!event || !event.target) return;
  let val = event.target.value;

  if (props.maxlength && typeof val === 'string' && props.type !== 'number') {
    val = val.slice(0, props.maxlength);
  }

  if (props.type === 'number') {
    val = sanitizeNumberInput(val);
  }

  event.target.value = val;
  internalValue.value = val;
  emit('update:modelValue', val);
};

const handleFocus = (event) => {
  focused.value = true;
  if (props.type === 'number' && inputRef.value) {
    const raw = sanitizeNumberInput(props.modelValue);
    inputRef.value.value = raw;
    internalValue.value = raw;
  }
  emit('focus', event);
};

const handleBlur = (event) => {
  focused.value = false;
  if (props.type === 'number') {
    const current = event?.target?.value ?? props.modelValue;
    const sanitized = finalizeNumber(current);
    internalValue.value = sanitized;
    emit('update:modelValue', sanitized);
    if (inputRef.value) {
      inputRef.value.value = formatThousands(sanitized);
    }
  }
  emit('blur', event);
};

const handleMouseEnter = () => {
  if (!props.disabled) {
    // hover state handled via CSS
  }
};

const handleMouseLeave = () => {
  if (!props.disabled) {
    // hover state handled via CSS
  }
};

const handleCopyClick = async () => {
  if (props.disabled || !internalValue.value) {
    return;
  }

  if (inputRef.value) {
    inputRef.value.focus();
    inputRef.value.select();
  }

  try {
    await navigator.clipboard.writeText(internalValue.value);
    emit('copy', internalValue.value);
    console.log('Text copied to clipboard:', internalValue.value);
  } catch (error) {
    console.error('Failed to copy text to clipboard:', error);
    try {
      if (inputRef.value) {
        document.execCommand('copy');
        emit('copy', internalValue.value);
        console.log('Text copied to clipboard (fallback):', internalValue.value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = String(internalValue.value ?? '');
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        emit('copy', internalValue.value);
        console.log('Text copied to clipboard (fallback textarea):', internalValue.value);
      }
    } catch (fallbackError) {
      console.error('Fallback copy method also failed:', fallbackError);
    }
  }
};
</script>

<style lang="scss" scoped>
.q-text-input__wrapper {
  overflow: hidden;
}
.q-text-input {
  display: flex;
  flex-direction: column;
  gap: 2px;

  &__wrapper {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    border: 1px solid var(--q-text-input-border-color, #e7e7e9);
    transition: all 0.2s ease;
  }

  &__input {
    width: 100%;
    border: none;
    outline: none;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    transition: all 0.2s ease;
    flex: 1;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:readonly {
      cursor: default;
    }
  }

  &__copy-icon {
    position: absolute;
    right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 1;

    &:hover {
      opacity: 0.7;
    }
  }

  // Large size
  &--l {
    .q-text-input__wrapper {
      border-radius: 16px;
      height: 52px;
    }

    .q-text-input__input {
      padding: 12px;
      font-size: 16px;
      line-height: 1.2;
    }

    &.q-text-input--with-copy .q-text-input__input {
      padding-right: 44px;
    }
  }

  // Medium size
  &--m {
    .q-text-input__wrapper {
      border-radius: 12px;
      height: 44px;
    }

    .q-text-input__input {
      padding: 12px;
      font-size: 16px;
      line-height: 1.2;
    }

    &.q-text-input--with-copy .q-text-input__input {
      padding-right: 44px;
    }
  }

  // Small size
  &--s {
    .q-text-input__wrapper {
      border-radius: 8px;
      height: 41px;
    }

    .q-text-input__input {
      padding: 12px;
      font-size: 14px;
      line-height: 1.2;
    }

    &.q-text-input--with-copy .q-text-input__input {
      padding-right: 44px;
    }
  }

  // Primary variant
  &--primary {
    --q-text-input-border-color: #e7e7e9;

    .q-text-input__wrapper {
      background-color: var(--color-white);
    }

    .q-text-input__input {
      color: rgba(14, 14, 34, 0.4);

      &::placeholder {
        color: rgba(14, 14, 34, 0.4);
      }
    }

    &:hover:not(.q-text-input--disabled) {
      --q-text-input-border-color: rgba(14, 14, 34, 0.14);

      .q-text-input__input {
        color: rgba(14, 14, 34, 0.6);

        &::placeholder {
          color: rgba(14, 14, 34, 0.6);
        }
      }
    }

    &.q-text-input--focused {
      --q-text-input-border-color: rgba(14, 14, 34, 0.2);

      .q-text-input__input {
        color: rgba(14, 14, 34, 0.9);

        &::placeholder {
          color: rgba(14, 14, 34, 0.9);
        }
      }
    }
  }

  // Secondary variant
  &--secondary {
    --q-text-input-border-color: rgba(14, 14, 34, 0.1);

    .q-text-input__wrapper {
      input {
        background-color: #f7f7f7 !important;
      }
    }

    .q-text-input__input {
      color: rgba(14, 14, 34, 0.4);

      &::placeholder {
        color: rgba(14, 14, 34, 0.4);
      }
    }

    &:hover:not(.q-text-input--disabled) {
      --q-text-input-border-color: rgba(14, 14, 34, 0.2);

      .q-text-input__input {
        color: rgba(14, 14, 34, 0.6);

        &::placeholder {
          color: rgba(14, 14, 34, 0.6);
        }
      }
    }

    &.q-text-input--focused {
      --q-text-input-border-color: rgba(14, 14, 34, 0.3);

      .q-text-input__input {
        color: rgba(14, 14, 34, 0.8);

        &::placeholder {
          color: rgba(14, 14, 34, 0.8);
        }
      }
    }
  }

  // Tertiary variant
  &--tertiary {
    .q-text-input__wrapper {
      background-color: transparent;
    }

    .q-text-input__input {
      color: rgba(14, 14, 34, 0.8);

      &::placeholder {
        color: rgba(14, 14, 34, 0.8);
      }
    }

    &:hover:not(.q-text-input--disabled) {
      .q-text-input__wrapper {
        background-color: rgba(14, 14, 34, 0.06);
      }
    }

    &.q-text-input--focused {
      .q-text-input__wrapper {
        background-color: rgba(14, 14, 34, 0.06);
      }
    }
  }

  // Error state
  &--error {
    .q-text-input__wrapper {
      border: 1px solid #f23f3f;
    }

    .q-text-input__input {
      color: #f23f3f;

      &::placeholder {
        color: #f23f3f;
      }
    }
  }

  // Disabled state
  &--disabled {
    .q-text-input__wrapper {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .q-text-input__copy-icon {
      pointer-events: none;
      opacity: 0.5;
    }
  }

  // Readonly state
  &--readonly {
    .q-text-input__wrapper {
      cursor: default;
    }

    .q-text-input__copy-icon {
      cursor: pointer;
    }
  }
  // Full width modifier
  &--full-width {
    width: 100%;
    max-width: none;
  }

  &__error {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.2;
    color: #f23f3f;
    margin-left: 4px;
  }
}
</style>
